import { storeToRefs } from 'pinia'
import {
  cartHasVoucherDiscount,
  getVoucherDiscountFromCart,
  resolveAdjustedDevicePrice,
  resolveDevicePriceForCartItem
} from '~/composable/voucher/resolveDevicePriceForCart'
import { useCartStore } from '~/stores/useCartStore'
import { useAppliedDiscountCodeStore } from '~/stores/voucher/useAppliedDiscountCodeStore'
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore'

const resolveWithoutCart = (cartItem) => {
  const selectedPrice = cartItem?.cartProduct?.selectedPrice
  if (!selectedPrice) {
    return 0
  }

  const { lineItemPrice, basePrice } = selectedPrice
  if (lineItemPrice !== undefined && lineItemPrice !== null) {
    return lineItemPrice
  }

  return basePrice || 0
}

const hasDefinedPrice = (price) => {
  return price !== undefined && price !== null
}

const applyVoucherDiscount = (price, voucherDiscount) => {
  return Math.max(0, price - (voucherDiscount || 0))
}

const resolveFromProductWithSubscriptionPrice = (bundle, voucherDiscount) => {
  if (!hasDefinedPrice(bundle.productWithSubscriptionPrice)) {
    return null
  }
  return applyVoucherDiscount(bundle.productWithSubscriptionPrice, voucherDiscount)
}

const resolveFromBasePriceAndDiscount = (bundle, voucherDiscount) => {
  if (!hasDefinedPrice(bundle.basePrice)) {
    return null
  }
  const productDiscount = bundle.discount ?? 0
  return applyVoucherDiscount(bundle.basePrice - productDiscount, voucherDiscount)
}

const resolveExpectedFromPdpBundle = (bundle, voucherDiscount) => {
  if (!bundle) {
    return null
  }
  return resolveFromProductWithSubscriptionPrice(bundle, voucherDiscount) ??
    resolveFromBasePriceAndDiscount(bundle, voucherDiscount)
}

const resolveFromCartWithVoucher = (cartItem, cart, storeVoucherDiscount) => {
  if (cart || storeVoucherDiscount !== null) {
    return resolveDevicePriceForCartItem(cartItem, cart, storeVoucherDiscount)
  }
  return resolveWithoutCart(cartItem)
}

const buildBundleFromCartItem = (cartItem) => {
  const selectedPrice = cartItem?.cartProduct?.selectedPrice
  if (!hasDefinedPrice(selectedPrice?.basePrice)) {
    return null
  }
  const discount = selectedPrice.discount ?? 0
  return {
    basePrice: selectedPrice.basePrice,
    discount,
    productWithSubscriptionPrice: Math.max(0, selectedPrice.basePrice - discount)
  }
}

/**
 * Resolves device/accessory line item price from a cart item.
 * Uses voucher-aware pricing when cart or discount-code store context is available.
 * Aligns with PDP PriceSummary when the same product is still selected in the store.
 */
export const useResolveVoucherDevicePrice = () => {
  let getCart = null
  let totalDiscountCodeDiscount = null
  let hasAppliedDiscountCode = null
  let productDetails = null
  let chosenChannel = null

  try {
    const cartStore = useCartStore()
    ;({ getCart } = storeToRefs(cartStore))
    const appliedDiscountCodeStore = useAppliedDiscountCodeStore()
    ;({ totalDiscountCodeDiscount, hasAppliedDiscountCode } = storeToRefs(appliedDiscountCodeStore))
    const chosenVariantDetailsStore = useChosenVariantDetailsStore()
    ;({ productDetails, chosenChannel } = storeToRefs(chosenVariantDetailsStore))
  } catch (e) {
    // Pinia not available (e.g. isolated unit tests)
  }

  const getStoreVoucherDiscount = () => {
    if (!hasAppliedDiscountCode?.value) {
      return null
    }
    return totalDiscountCodeDiscount?.value || 0
  }

  const matchesCurrentPdpProduct = (cartItem) => {
    const sku = cartItem?.cartProduct?.sku
    return !!sku && sku === productDetails?.value?.chosenVariantSku
  }

  const hasVoucherContext = (cart) => {
    if (hasAppliedDiscountCode?.value) {
      return true
    }
    return cartHasVoucherDiscount(cart)
  }

  const getVoucherDiscountAmount = (cart) => {
    if (hasAppliedDiscountCode?.value) {
      return totalDiscountCodeDiscount?.value || 0
    }
    return getVoucherDiscountFromCart(cart)
  }

  const getLivePdpBundle = (cartItem) => {
    if (!matchesCurrentPdpProduct(cartItem)) {
      return null
    }
    return chosenChannel?.value?.productBundlePrice || null
  }

  const getProductBundlePriceForCartItem = (cartItem) => {
    const liveBundle = getLivePdpBundle(cartItem)
    if (liveBundle) {
      return liveBundle
    }
    return buildBundleFromCartItem(cartItem)
  }

  const resolveFromBundlePrice = (cartItem, voucherDiscount) => {
    const bundle = getProductBundlePriceForCartItem(cartItem)
    if (!bundle) {
      return null
    }
    const expectedPrice = resolveExpectedFromPdpBundle(bundle, voucherDiscount)
    if (expectedPrice === null) {
      return null
    }
    const lineItemPrice = cartItem?.cartProduct?.selectedPrice?.lineItemPrice ?? null
    return resolveAdjustedDevicePrice(expectedPrice, lineItemPrice)
  }

  const tryResolveVoucherAdjustedPrice = (cartItem, cart) => {
    if (!hasVoucherContext(cart)) {
      return null
    }
    return resolveFromBundlePrice(cartItem, getVoucherDiscountAmount(cart))
  }

  const resolveFromCartItem = (cartItem, cartOverride = null) => {
    if (!cartItem?.cartProduct?.selectedPrice) {
      return 0
    }

    const cart = cartOverride || getCart?.value
    const voucherAdjustedPrice = tryResolveVoucherAdjustedPrice(cartItem, cart)
    if (voucherAdjustedPrice !== null) {
      return voucherAdjustedPrice
    }

    return resolveFromCartWithVoucher(cartItem, cart, getStoreVoucherDiscount())
  }

  const getPdpProductBundlePrice = (cartItem) => {
    return getProductBundlePriceForCartItem(cartItem)
  }

  return {
    resolveFromCartItem,
    getPdpProductBundlePrice
  }
}
